/* eslint jsx-a11y/label-has-associated-control:0 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  Arrow,
  getTranslation,
  Icon,
  Label,
  Loader,
  Radio,
  RadioGroup,
  SubmitButton,
  TextInput,
  TranslationProvider,
} from '@resy/ui-kitchen';
import Logger from '../../../../../ui-kitchen/src/utils/logger';

import './PrepayStart.scss';

const noop = () => {};
const testId = 'prepay-start';

const prepayStartPropTypes = {
  $routeParams: PropTypes.shape({
    token: PropTypes.string.isRequired,
  }).isRequired,
  $location: PropTypes.shape({
    url: PropTypes.func.isRequired,
  }).isRequired,
  Prepay: PropTypes.shape({
    get: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
  }).isRequired,
};

const fieldInitialValues = {
  amount: false,
  amount_type: false,
  description: false,
  gratuity_percentage: false,
  is_surprise: false,
  message: false,
  timing: false,
  timing_message: false,
};

const PrepayStart = ({ $location, $routeParams, Prepay }) => {
  const [fieldValues, setFieldValues] = useState(fieldInitialValues);
  const [touchedFields, setTouchedFields] = useState(fieldInitialValues);
  const [messaging, setMessaging] = useState();
  const [processing, setProcessing] = useState(false);
  const [reservation, setReservation] = useState({});
  const [venueCurrency, setVenueCurrency] = useState();
  const [venueName, setVenueName] = useState('');

  const { token } = $routeParams;

  useEffect(() => {
    const getPrepayData = async () => {
      try {
        const data = await Prepay.get(token);
        setFieldValues(data.prepay);
        setReservation(data.reservation);
        setVenueCurrency(data.venue?.currency_symbol);
        setVenueName(data.venue?.name || 'this venue');

        if (data.is_editable === 0) {
          $location.url(`/prepay/${token}/confirmation`);
        }
      } catch (error) {
        Logger.error('[CATCH] Prepay Start Controller: Prepay.get: ', error);
      }
    };

    getPrepayData();
  }, [$location, Prepay, token]);

  const getButtonContent = () => {
    if (processing) {
      return <Loader />;
    }
    return getTranslation({ translationKey: 'checkout', fallback: 'Checkout' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setProcessing(true);

    try {
      await Prepay.submit(token, fieldValues);
      $location.url(`/prepay/${token}/payment-method-stripe`);
    } catch (error) {
      setProcessing(false);
      setMessaging(getTranslation({
        translationKey: 'generic_submission_error',
        fallback: 'There was a problem processing your request. Please try again.',
      }));
    }
  };

  const handleFieldBlur = (event) => {
    setTouchedFields({
      ...touchedFields,
      [event.target.name]: true,
    });
  };

  const handleFieldChange = (event) => {
    setFieldValues({
      ...fieldValues,
      ...event,
    });
  };

  const handleFieldSyntheticChange = (event) => {
    handleFieldChange({ [event.target.name]: event.target.value });
  };

  const renderTimingWarning = () => {
    const isUnselected = touchedFields.timing && (!fieldValues.timing || fieldValues.timing === 'null');
    const isOtherSelected = fieldValues.timing === 'other';
    const isOtherUnfilled = touchedFields.timing_message && !fieldValues.timing_message;

    if (isUnselected || (isOtherSelected && isOtherUnfilled)) {
      return (
        <TranslationProvider translationKey="prepay_timing_other_message_error">
          <div className="PrepayStart__error-message">Please, tell us when you&apos;d like the guest to be informed.</div>
        </TranslationProvider>
      );
    }
    return null;
  };

  if (!reservation.first_name) {
    return <Loader />;
  }

  const gratuityAsString = fieldValues?.gratuity_percentage?.toString();
  const gratuityIsZero = fieldValues?.gratuity_percentage === null || gratuityAsString === '0';

  return (
    <div className="PrepayStart">
      <div className="PrepayStart__container">
        <header>
          <TranslationProvider translationKey="prepay_start_header">
            <h1>Secure Payment</h1>
          </TranslationProvider>

          <TranslationProvider translationKey="prepay_start_instructions" translationValues={{ venueName }}>
            <div className="PrepayStart__description">
              Please complete the secure form below to complete your transaction at
              {' '}
              {venueName}
              . If you have any questions, please email
              {' '}
              <a href="mailto:support@resy.com?subject=3rd Party Pre-Pay">support@resy.com</a>
              .
            </div>
          </TranslationProvider>
        </header>

        <hr />

        <form onSubmit={handleSubmit}>
          <div className="PrepayStart__form-field">
            <TranslationProvider translationKey="prepay_reservation_name_label">
              <Label forInput="reservation_name">Reservation name</Label>
            </TranslationProvider>
            <TextInput
              value={`${reservation.first_name} ${reservation.last_name}`}
              name="reservation_name"
              onChange={noop}
              disabled
            />
          </div>
          <div className="PrepayStart__form-field">
            <Label forInput="description" required>
              <TranslationProvider translationKey="prepay_item_description_label">
                Item description
              </TranslationProvider>
            </Label>

            <textarea
              name="description"
              className={cx({ 'PrepayStart__form-field--error': touchedFields.description && !fieldValues.description })}
              onBlur={handleFieldBlur}
              onChange={handleFieldSyntheticChange}
              placeholder={getTranslation({
                translationKey: 'prepay_item_description_placeholder',
                fallback: 'Bottle of wine, dessert...',
              })}
              value={fieldValues.description || ''}
              required
              data-test-id={`${testId}-description`}
            />

            {touchedFields.description && !fieldValues.description && (
              <TranslationProvider translationKey="prepay_item_description_error">
                <div className="PrepayStart__error-message">Please add an item description.</div>
              </TranslationProvider>
            )}
          </div>

          <div className="PrepayStart__form-field PrepayStart__form-field--amount">
            <Label forInput="amount" required>
              <TranslationProvider translationKey="prepay_amount_label">
                Subtotal (including tax)
              </TranslationProvider>
            </Label>

            <div className="PrepayStart__form-field--amount__row">
              <div className="PrepayStart__form-field--amount__input-container">
                {venueCurrency && <span className="PrepayStart__form-field--amount__currency">{venueCurrency}</span>}
                <input
                  className={cx({
                    'PrepayStart__form-field--error': touchedFields.amount && !fieldValues.amount,
                    'PrepayStart__form-field--amount--with-currency': venueCurrency,
                  })}
                  onBlur={handleFieldBlur}
                  onChange={handleFieldSyntheticChange}
                  name="amount"
                  type="number"
                  step=".01"
                  placeholder="0.00"
                  required
                  value={fieldValues.amount}
                  data-test-id={`${testId}-amount`}
                />
              </div>
              <div className="PrepayStart__radio-group">
                <RadioGroup onChange={handleFieldChange}>
                  <Radio
                    checked={fieldValues.amount_type === 'fixed'}
                    className={cx({ 'PrepayStart__radio--active': fieldValues.amount_type === 'fixed' })}
                    value={{ amount_type: 'fixed' }}
                    data-test-id={`${testId}-amount-type-fixed`}
                  >
                    {getTranslation({ translationKey: 'prepay_amount_type_label_fixed', fallback: 'Fixed' })}
                  </Radio>
                  <Radio
                    checked={fieldValues.amount_type === 'maximum'}
                    className={cx({ 'PrepayStart__radio--active': fieldValues.amount_type === 'maximum' })}
                    value={{ amount_type: 'maximum' }}
                    data-test-id={`${testId}-amount-type-maximum`}
                  >
                    {getTranslation({ translationKey: 'prepay_amount_type_label_maximum', fallback: 'Max' })}
                  </Radio>
                </RadioGroup>
              </div>
            </div>

            <div className="PrepayStart__form-field__description">
              {getTranslation({ translationKey: 'prepay_amount_description', fallback: 'It can be a fixed or maximum amount.' })}
            </div>

            {touchedFields.amount && !fieldValues.amount && (
              <TranslationProvider translationKey="prepay_amount_error">
                <div className="PrepayStart__error-message">
                  Please add an amount.
                </div>
              </TranslationProvider>
            )}
          </div>
          <div className="PrepayStart__form-field">
            <TranslationProvider translationKey="prepay_gratuity_label">
              <Label id="gratuity_percentage_label">Add Gratuity?</Label>
            </TranslationProvider>

            <div className="PrepayStart__radio-group">
              <RadioGroup onChange={handleFieldChange} aria-labelledby="gratuity_percentage_label">
                <Radio
                  checked={gratuityIsZero}
                  className={cx({ 'PrepayStart__radio--active': gratuityIsZero })}
                  value={{ gratuity_percentage: 0 }}
                  data-test-id={`${testId}-gratuity-0`}
                >
                  {getTranslation({ translationKey: 'prepay_gratuity_percentage_0', fallback: 'None' })}
                </Radio>
                <Radio
                  checked={gratuityAsString === '18'}
                  className={cx({ 'PrepayStart__radio--active': gratuityAsString === '18' })}
                  value={{ gratuity_percentage: 18 }}
                  data-test-id={`${testId}-gratuity-18`}
                >
                  18%
                </Radio>
                <Radio
                  checked={gratuityAsString === '20'}
                  className={cx({ 'PrepayStart__radio--active': gratuityAsString === '20' })}
                  value={{ gratuity_percentage: 20 }}
                  data-test-id={`${testId}-gratuity-20`}
                >
                  20%
                </Radio>
                <Radio
                  checked={gratuityAsString === '22'}
                  className={cx({ 'PrepayStart__radio--active': gratuityAsString === '22' })}
                  value={{ gratuity_percentage: 22 }}
                  data-test-id={`${testId}-gratuity-22`}
                >
                  22%
                </Radio>
              </RadioGroup>
            </div>

            <div className="PrepayStart__form-field__description">
              {getTranslation({ translationKey: 'prepay_gratuity_description', fallback: 'This will be added to the amount.' })}
            </div>

          </div>
          <div className="PrepayStart__form-field">
            <TranslationProvider translationKey="prepay_is_surprise_label">
              <Label forInput="is_surprise">Is this a surprise?</Label>
            </TranslationProvider>
            <div className="PrepayStart__radio-group">
              <RadioGroup onChange={handleFieldChange}>
                <Radio
                  value={{ is_surprise: 0 }}
                  checked={!parseInt(fieldValues.is_surprise, 10)}
                  className={cx({ 'PrepayStart__radio--active': parseInt(fieldValues.is_surprise, 10) === 0 })}
                  data-test-id={`${testId}-surprise-no`}
                >
                  {getTranslation({ translationKey: 'no', fallback: 'No' })}
                </Radio>
                <Radio
                  value={{ is_surprise: 1 }}
                  checked={!!parseInt(fieldValues.is_surprise, 10)}
                  className={cx({ 'PrepayStart__radio--active': parseInt(fieldValues.is_surprise, 10) === 1 })}
                  data-test-id={`${testId}-surprise-yes`}
                >
                  {getTranslation({ translationKey: 'yes', fallback: 'Yes' })}
                </Radio>
              </RadioGroup>
            </div>
          </div>

          <div className="PrepayStart__form-field PrepayStart__form-field--timing">
            <TranslationProvider translationKey="prepay_timing_label">
              <Label forInput="timing">When should the guest(s) be informed?</Label>
            </TranslationProvider>
            <div className="PrepayStart__timing-container">
              <select
                className={cx({ 'PrepayStart__form-field--error': touchedFields.timing && (!fieldValues.timing || fieldValues.timing === 'null') })}
                defaultValue={fieldValues.timing}
                onBlur={handleFieldBlur}
                onChange={handleFieldSyntheticChange}
                name="timing"
                data-test-id={`${testId}-timing`}
              >
                <option value="null">
                  {getTranslation({ translationKey: 'prepay_timing_null', fallback: 'Select timing...' })}
                </option>
                <option value="when_seated">
                  {getTranslation({ translationKey: 'prepay_timing_when_seated', fallback: 'When seated' })}
                </option>
                <option value="when_delivered">
                  {getTranslation({ translationKey: 'prepay_timing_when_delivered', fallback: 'When delivered' })}
                </option>
                <option value="end_of_meal">
                  {getTranslation({ translationKey: 'prepay_timing_end_of_meal', fallback: 'End of meal' })}
                </option>
                <option value="other">
                  {getTranslation({ translationKey: 'prepay_timing_other', fallback: 'Other' })}
                </option>
              </select>
              <Icon component={Arrow} name="Chevron" />
            </div>

            {fieldValues.timing === 'other' && (
              <TextInput
                name="timing_message"
                className={cx({ 'PrepayStart__form-field--error': touchedFields.timing_message && !fieldValues.timing_message })}
                onBlur={handleFieldBlur}
                onChange={handleFieldChange}
                placeholder={getTranslation({
                  translationKey: 'prepay_timing_other_message_placeholder',
                  fallback: 'Tell us when...',
                })}
                required
                value={fieldValues.timing_message}
                data-test-id={`${testId}-timing-message`}
              />
            )}

            {renderTimingWarning()}
          </div>

          <div className="PrepayStart__form-field PrepayStart__form-field--optional-message">
            <TranslationProvider translationKey="prepay_message_label">
              <Label forInput="message">Optional Message</Label>
            </TranslationProvider>
            <textarea
              name="message"
              onChange={handleFieldSyntheticChange}
              placeholder={getTranslation({
                translationKey: 'prepay_message_placeholder',
                fallback: 'E.g., who is this from?',
              })}
              value={fieldValues.message || ''}
              data-test-id={`${testId}-optional-message`}
            />
          </div>

          {messaging && (
            <div className="messages">
              <div className="alert">{messaging}</div>
            </div>
          )}

          <SubmitButton
            disabled={processing}
            lg
            onClick={noop}
            data-test-id={`${testId}-submit`}
          >
            {getButtonContent()}
          </SubmitButton>
        </form>
      </div>
    </div>
  );
};

PrepayStart.propTypes = prepayStartPropTypes;

export { prepayStartPropTypes };
export default PrepayStart;
